'use client';
import { configureStore } from '@reduxjs/toolkit';
import reducers from './reducers/combine-all.reducers';
import { setCookie } from '@/utils';

// The cart otherwise lives only in memory, so a mobile browser reloading the
// tab (backgrounding it while the user switches apps to read an OTP, low
// memory, a flaky network) silently wipes out an in-progress ticket
// selection. sessionStorage survives that kind of reload (same tab/session)
// without persisting across a real close.
//
// It must NOT be read into preloadedState: the server has no sessionStorage, so
// seeding it at store creation would diverge from the server render and break
// hydration. ReduxProvider restores it client-side in a useEffect instead.
export const CART_STORAGE_KEY = 'greet_cart';

/**
 * One store PER RENDER, not one per process.
 *
 * The store used to be a module-level singleton. On the client that is
 * harmless; during SSR it is not, because a module holds its value for the
 * lifetime of the Node process and every request would have shared - and
 * written to - the same state.
 *
 * `preloadedState` is what fixes the location hydration mismatch. The
 * location reducer seeds itself from the `user_location` cookie, which it can
 * only read in a browser, so the server rendered the DEFAULT city and the
 * client rendered the COOKIE's city and React threw away the tree. The server
 * now reads the same cookie from the request and passes it in, so both sides
 * start from one value.
 */
export const makeStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: reducers,
    preloadedState: preloadedState as never,
  });

  // Persist the chosen location so the NEXT server render can seed from it.
  // Browser only: setCookie writes document.cookie.
  if (typeof document !== 'undefined') {
    store.subscribe(() => {
      const locationState = store.getState().location;

      if (locationState) {
        setCookie('user_location', JSON.stringify(locationState), 365);
      }

      // Keep the cart across a mobile tab reload (see CART_STORAGE_KEY).
      try {
        const cart = store.getState().user?.cart;
        if (cart) {
          sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
        } else {
          sessionStorage.removeItem(CART_STORAGE_KEY);
        }
      } catch {
        // sessionStorage can throw (private browsing, quota) - the cart just
        // won't survive a reload in that case.
      }
    });
  }

  return store;
};

export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<typeof reducers>;
export type AppDispatch = AppStore['dispatch'];
